/**
 * One cue in the list (1.9.0), shared by the asset Subtitles tab and the sequence editor: start and end
 * CueTimecodeFields, the text area, the live counters (e.g. "16.2 cps · 38/42 · 2 lines", amber over the rules) with
 * inline issues such as "Overlaps the next cue", the grid and style buttons, and delete. Shift-click selects several.
 */
import { memo } from 'react';
import { Trash2 } from 'lucide-react';
import { effectiveCueStyle, legacyAlign, type Cue, type CueMetrics, type CueStylePatch, type Rational, type SubtitleRules, type TrackStyle } from '@prism/contracts';
import type { CueEdge } from './types';
import { CueTimecodeField } from './CueTimecodeField';
import { PlacementGrid } from './PlacementGrid';
import { CueStylePopover } from './CueStylePopover';
import { IS_MAC, subtitleKeyAction } from './keys';
import { toggleInlineTag } from './textEdit';

export interface CueRowProps {
  cue: Cue;
  index: number;
  rate: Rational;
  /** The timecode at time 0 in frames (the media's start timecode; 0 in a sequence). */
  startFrames: number;
  /** 1.9.1: the media's length (CueTimecodeField, past midnight). */
  durationTicks?: number | null;
  trackStyle: TrackStyle;
  /** contracts cueMetrics for this cue, with the whole track (same-row overlaps). */
  metrics: CueMetrics;
  rules: SubtitleRules;
  selected: boolean;
  /** 1.9.3: the cue shows at the playhead (class at-playhead, data-playhead); a cue can be selected as well. */
  atPlayhead?: boolean;
  /** The edge the nudge keys move (the last set with ⌥I/⌥O or clicked). */
  activeEdge: CueEdge | null;
  readOnly?: boolean;
  onSelect(opts: { extend: boolean }): void;
  /** A typed start or end, in ticks (the caller applies contracts setCueEdge). */
  onEdge(edge: CueEdge, ticks: number): void;
  onActiveEdge(edge: CueEdge): void;
  onText(text: string): void;
  onStyle(patch: CueStylePatch | null): void;
  onDelete(): void;
  /** The text area, for ⌘I / ⌘B on the selected words and the split caret (⌥S). */
  textAreaRef?: (el: HTMLTextAreaElement | null) => void;
  testId?: string;
}

/** The issues the counters already show. */
const COUNTED = new Set(['reading_speed', 'line_length', 'too_many_lines']);

/** "18.1 cps · 38/42 · 1 line", and which parts are over the rules. */
export function counterParts(m: CueMetrics, rules: SubtitleRules) {
  return {
    cps: { text: `${m.cps.toFixed(1)} cps`, over: m.cps > rules.maxCharsPerSecond },
    line: { text: `${m.longestLine}/${rules.maxCharsPerLine}`, over: m.longestLine > rules.maxCharsPerLine },
    lines: { text: `${m.lines} ${m.lines === 1 ? 'line' : 'lines'}`, over: m.lines > rules.maxLines },
  };
}

function CueRowImpl({ cue, index, rate, startFrames, durationTicks, trackStyle, metrics, rules, selected, atPlayhead = false, activeEdge, readOnly, onSelect, onEdge, onActiveEdge, onText, onStyle, onDelete, textAreaRef, testId = 'cue-row' }: CueRowProps) {
  const track = effectiveCueStyle(trackStyle, { text: '' });
  const counters = counterParts(metrics, rules);
  const errors = metrics.issues.filter((i) => i.severity === 'error');
  const others = metrics.issues.filter((i) => !COUNTED.has(i.kind));
  const state = errors.length ? 'error' : metrics.issues.length ? 'warn' : '';
  return (
    <div className={`cue-row v19 ${selected ? 'selected' : ''} ${atPlayhead ? 'at-playhead' : ''} ${state}`} data-cue={cue.id} data-index={index} data-testid={testId} aria-selected={selected}
      data-playhead={atPlayhead || undefined}
      onMouseDown={(e) => { if (e.shiftKey && !(e.target as Element).closest('textarea, input')) e.preventDefault(); }}
      onClick={(e) => { if ((e.target as Element).closest('.sub-popover, [aria-haspopup]')) return; onSelect({ extend: e.shiftKey || e.metaKey || e.ctrlKey }); }}>
      <div className="cue-times">
        <span className="cue-index faint mono">{index + 1}</span>
        <span className={`cue-edge ${selected && activeEdge === 'start' ? 'active' : ''}`}>
          <CueTimecodeField ticks={cue.startTicks} rate={rate} startFrames={startFrames} durationTicks={durationTicks} label="Start" readOnly={readOnly} testId="cue-start"
            onFocus={() => onActiveEdge('start')} onCommit={(t) => onEdge('start', t)} />
        </span>
        <span className="faint">→</span>
        <span className={`cue-edge ${selected && activeEdge === 'end' ? 'active' : ''}`}>
          <CueTimecodeField ticks={cue.endTicks} rate={rate} startFrames={startFrames} durationTicks={durationTicks} label="End" readOnly={readOnly} testId="cue-end"
            onFocus={() => onActiveEdge('end')} onCommit={(t) => onEdge('end', t)} />
        </span>
        <span style={{ flex: 1 }} />
        <PlacementGrid value={cue.style?.align ?? legacyAlign(cue.text) ?? null} trackAlign={track.align}
          onChange={(a) => onStyle({ align: a })} disabled={readOnly} testId="cue-grid" />
        <CueStylePopover value={cue.style ?? {}} track={track} onChange={onStyle} disabled={readOnly} testId="cue-style" />
        {!readOnly && <button type="button" className="btn ghost icon sm" aria-label="Delete cue" title="Delete cue" onClick={(e) => { e.stopPropagation(); onDelete(); }}><Trash2 size={12} /></button>}
      </div>
      <textarea className="textarea" dir="auto" rows={2} value={cue.text} readOnly={readOnly} aria-label="Cue text" data-cue-text={cue.id} ref={textAreaRef}
        onFocus={() => { if (!selected) onSelect({ extend: false }); }}
        onChange={(e) => onText(e.target.value)}
        onKeyDown={(e) => {
          const a = subtitleKeyAction(e.nativeEvent, IS_MAC);
          if (!a || (a.action !== 'italic' && a.action !== 'bold') || readOnly) return;
          e.preventDefault();
          e.stopPropagation();
          const el = e.currentTarget;
          const r = toggleInlineTag(el.value, el.selectionStart, el.selectionEnd, a.action === 'italic' ? 'i' : 'b');
          if (r.text === el.value) return;
          onText(r.text);
          requestAnimationFrame(() => { try { el.setSelectionRange(r.start, r.end); } catch { /* gone */ } });
        }} />
      <div className="cue-counters mono" data-testid="cue-counters">
        <span className={counters.cps.over ? 'over' : ''} data-testid="cue-cps">{counters.cps.text}</span> · <span className={counters.line.over ? 'over' : ''}>{counters.line.text}</span> · <span className={counters.lines.over ? 'over' : ''}>{counters.lines.text}</span>
        {others.map((i) => <span key={i.kind + i.message} className={i.severity === 'error' ? 'error-text' : 'warn-text'}> · {i.message}</span>)}
      </div>
    </div>
  );
}

export const CueRow = memo(CueRowImpl);
